/**
 * Google Gemini SDK adapter for morph-warp-grep tool
 * 
 * Requires @google/generative-ai as a peer dependency.
 * Install with: npm install @google/generative-ai
 */

import type { FunctionDeclaration, FunctionDeclarationSchema } from '@google/generative-ai';
import { executeToolCall, formatResult, WarpGrepClient, executeGitHubReadFile, formatGitHubReadFileResult } from './client.js';
import { WARP_GREP_DESCRIPTION, WARP_GREP_TOOL_NAME, GITHUB_SEARCH_TOOL_NAME, GITHUB_SEARCH_DESCRIPTION, GITHUB_READ_FILE_TOOL_NAME, GITHUB_READ_FILE_DESCRIPTION, GITHUB_READ_FILE_INPUT_SCHEMA } from './prompts.js';
import type { WarpGrepToolConfig, WarpGrepResult, GitHubSearchToolConfig, GitHubReadFileInput, GitHubReadFileResult, GitHubReadFileToolConfig } from './types.js';

// Use plain object to avoid runtime import of SchemaType enum
const TOOL_PARAMETERS = {
  type: 'OBJECT',
  properties: {
    search_term: { 
      type: 'STRING', 
      description: 'Search problem statement that this subagent is supposed to research for' 
    },
  },
  required: ['search_term'],
} as unknown as FunctionDeclarationSchema;

/**
 * Gemini-native warp grep function declaration
 * 
 * @example
 * ```typescript
 * import { GoogleGenerativeAI } from '@google/generative-ai';
 * import { warpGrepFunctionDeclaration, execute } from '@morphllm/morphsdk/tools/warp-grep/gemini';
 * 
 * const genAI = new GoogleGenerativeAI(process.env.GOOGLE_API_KEY);
 * const model = genAI.getGenerativeModel({
 *   model: 'gemini-2.0-flash',
 *   tools: [{ functionDeclarations: [warpGrepFunctionDeclaration] }]
 * });
 * 
 * const chat = model.startChat();
 * const result = await chat.sendMessage('Find authentication middleware');
 * 
 * // Handle function call
 * const call = result.response.functionCalls()?.[0];
 * if (call) {
 *   const searchResult = await execute(call.args, { repoRoot: '.' });
 *   console.log(searchResult);
 * }
 * ```
 */
export const warpGrepFunctionDeclaration: FunctionDeclaration = {
  name: WARP_GREP_TOOL_NAME,
  description: WARP_GREP_DESCRIPTION,
  parameters: TOOL_PARAMETERS,
};

/**
 * Execute warp grep search
 * 
 * @param input - Tool input with search_term
 * @param config - Configuration with repoRoot and optional provider
 * @returns Search results
 */
export async function execute(
  input: { search_term: string } | string,
  config: WarpGrepToolConfig
): Promise<WarpGrepResult> {
  return executeToolCall(input, config);
}

export { formatResult };

/**
 * Gemini tool with execute method attached
 */
export interface GeminiWarpGrepTool extends FunctionDeclaration {
  execute: (input: unknown) => Promise<WarpGrepResult>;
  formatResult: (result: WarpGrepResult) => string;
}

/**
 * Create a custom warp grep tool with configuration and methods
 * 
 * @param config - Configuration options
 * @returns Function declaration with execute and formatResult methods
 * 
 * @example Local usage
 * ```typescript
 * import { GoogleGenerativeAI } from '@google/generative-ai';
 * import { createMorphWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/gemini';
 * 
 * const tool = createMorphWarpGrepTool({ repoRoot: '.' });
 * 
 * const genAI = new GoogleGenerativeAI(process.env.GOOGLE_API_KEY);
 * const model = genAI.getGenerativeModel({
 *   model: 'gemini-2.0-flash',
 *   tools: [{ functionDeclarations: [tool] }]
 * });
 * 
 * const chat = model.startChat();
 * const result = await chat.sendMessage('Find authentication middleware');
 * 
 * // Handle function call
 * const call = result.response.functionCalls()?.[0];
 * if (call && call.name === tool.name) {
 *   const searchResult = await tool.execute(call.args);
 *   console.log(tool.formatResult(searchResult));
 *   
 *   // Send result back to model
 *   await chat.sendMessage([{
 *     functionResponse: {
 *       name: call.name,
 *       response: { result: tool.formatResult(searchResult) }
 *     }
 *   }]);
 * }
 * ```
 * 
 * @example Remote sandbox (E2B, Modal, etc.)
 * ```typescript
 * const tool = createMorphWarpGrepTool({
 *   repoRoot: '/home/repo',
 *   remoteCommands: {
 *     grep: async (pattern, path) => (await sandbox.run(`rg '${pattern}' '${path}'`)).stdout,
 *     read: async (path, start, end) => (await sandbox.run(`sed -n '${start},${end}p' '${path}'`)).stdout,
 *     listDir: async (path, maxDepth) => (await sandbox.run(`find '${path}' -maxdepth ${maxDepth}`)).stdout,
 *   },
 * });
 * ```
 */
export function createWarpGrepTool(config: WarpGrepToolConfig): GeminiWarpGrepTool {
  const declaration: FunctionDeclaration = {
    name: config.name ?? WARP_GREP_TOOL_NAME,
    description: config.description ?? WARP_GREP_DESCRIPTION,
    parameters: TOOL_PARAMETERS,
  };

  return Object.assign(declaration, {
    execute: async (input: unknown): Promise<WarpGrepResult> => {
      return executeToolCall(input as { search_term: string } | string, config);
    },
    formatResult: (result: WarpGrepResult): string => {
      return formatResult(result);
    },
  });
}

// Legacy alias for backwards compatibility
export const createMorphWarpGrepTool = createWarpGrepTool;

const GITHUB_SEARCH_PARAMETERS = {
  type: 'OBJECT',
  properties: {
    search_term: { type: 'STRING', description: 'Search problem statement that this subagent is supposed to research for' },
    github_url: { type: 'STRING', description: 'GitHub repository URL to search (e.g. "https://github.com/vercel/next.js"). You must provide either github_url or owner_repo.' },
    owner_repo: { type: 'STRING', description: 'Repository owner/repo shorthand (e.g. "vercel/next.js"). You must provide either github_url or owner_repo.' },
    branch: { type: 'STRING', description: 'Branch to search (defaults to repo default branch)' },
  },
  required: ['search_term'],
} as unknown as FunctionDeclarationSchema;

/**
 * Create a GitHub search tool for Google Gemini SDK
 *
 * @param config - Configuration options (morphApiKey, morphApiUrl, codeSearchUrl, timeout)
 * @returns Gemini FunctionDeclaration with execute and formatResult methods
 *
 * @example
 * ```typescript
 * import { GoogleGenerativeAI } from '@google/generative-ai';
 * import { createGitHubSearchTool } from '@morphllm/morphsdk/tools/warp-grep/gemini';
 *
 * const tool = createGitHubSearchTool({ morphApiKey: process.env.MORPH_API_KEY });
 *
 * const genAI = new GoogleGenerativeAI(process.env.GOOGLE_API_KEY);
 * const model = genAI.getGenerativeModel({
 *   model: 'gemini-2.0-flash',
 *   tools: [{ functionDeclarations: [tool] }]
 * });
 *
 * // Execute
 * const result = await tool.execute({ search_term: 'auth middleware', github_url: 'https://github.com/vercel/next.js' });
 * console.log(tool.formatResult(result));
 * ```
 */
export function createGitHubSearchTool(config: GitHubSearchToolConfig) {
  const client = new WarpGrepClient(config);

  const declaration: FunctionDeclaration = {
    name: GITHUB_SEARCH_TOOL_NAME,
    description: GITHUB_SEARCH_DESCRIPTION,
    parameters: GITHUB_SEARCH_PARAMETERS,
  };

  return Object.assign(declaration, {
    execute: async (input: { search_term: string; github_url?: string; owner_repo?: string; branch?: string }): Promise<WarpGrepResult> => {
      const github = input.github_url || input.owner_repo;
      if (!github) {
        throw new Error('Please provide github search url or owner/repo id');
      }
      return client.searchGitHub({ searchTerm: input.search_term, github, branch: input.branch });
    },
    formatResult: (result: WarpGrepResult): string => {
      return formatResult(result);
    },
  });
}

const GITHUB_READ_FILE_PARAMETERS = {
  type: 'OBJECT',
  properties: {
    github: { type: 'STRING', description: 'owner/repo shorthand (e.g., "vercel/next.js")' },
    path: { type: 'STRING', description: 'File path within the repository' },
    startLine: { type: 'NUMBER', description: 'Start line number (1-based)' },
    endLine: { type: 'NUMBER', description: 'End line number (1-based, inclusive)' },
    branch: { type: 'STRING', description: 'Branch to read from (defaults to repo default branch)' },
  },
  required: ['github', 'path'],
} as unknown as FunctionDeclarationSchema;

/**
 * Create a GitHub read file tool for Google Gemini SDK
 *
 * @param config - Optional configuration (timeout)
 * @returns Gemini FunctionDeclaration with execute and formatResult methods
 *
 * @example
 * ```typescript
 * import { GoogleGenerativeAI } from '@google/generative-ai';
 * import { createGitHubReadFileTool } from '@morphllm/morphsdk/tools/warp-grep/gemini';
 *
 * const tool = createGitHubReadFileTool();
 *
 * const genAI = new GoogleGenerativeAI(process.env.GOOGLE_API_KEY);
 * const model = genAI.getGenerativeModel({
 *   model: 'gemini-2.0-flash',
 *   tools: [{ functionDeclarations: [tool] }]
 * });
 *
 * const result = await tool.execute({ github: 'vercel/next.js', path: 'package.json' });
 * console.log(tool.formatResult(result));
 * ```
 */
export function createGitHubReadFileTool(config?: GitHubReadFileToolConfig) {
  const declaration: FunctionDeclaration = {
    name: GITHUB_READ_FILE_TOOL_NAME,
    description: GITHUB_READ_FILE_DESCRIPTION,
    parameters: GITHUB_READ_FILE_PARAMETERS,
  };

  return Object.assign(declaration, {
    execute: async (input: GitHubReadFileInput): Promise<GitHubReadFileResult> => {
      return executeGitHubReadFile(input, config);
    },
    formatResult: (result: GitHubReadFileResult): string => {
      return formatGitHubReadFileResult(result);
    },
  });
}

export default warpGrepFunctionDeclaration;

